import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Compass, Mail, MessageSquare, CalendarDays, Users, CreditCard, Calculator,
  Megaphone, RefreshCw, Lightbulb, ArrowRight,
} from "lucide-react";
import { PageHeader, SectionCard } from "../components/ui/kaizen";
import { PUBLIC_HOST } from "../lib/domain";

// Aide-mémoire « où aller pour... » — édité à la main (aucun coût IA).
const TASKS = [
  {
    icon: Mail, title: "Envoyer un courriel URGENT à UN client",
    desc: "Va dans Diffusion membres → onglet Composer → Audience « Liste précise » → tape son courriel → envoie.",
    to: "/app/diffusion-courriel", cta: "Ouvrir Diffusion membres",
  },
  {
    icon: Megaphone, title: "Envoyer un courriel à TOUS les membres",
    desc: "Diffusion membres → Composer → Audience « Membres actifs » ou « Tous les contacts ». Modèle « Horaire d'été » déjà prêt.",
    to: "/app/diffusion-courriel", cta: "Ouvrir Diffusion membres",
  },
  {
    icon: MessageSquare, title: "Envoyer un SMS à tous (NOUVEAU)",
    desc: "Diffusion SMS → choisis l'audience, écris le message (sans emoji = moins cher), teste à ton numéro, envoie via Twilio.",
    to: "/app/diffusion-sms", cta: "Ouvrir Diffusion SMS",
  },
  {
    icon: CreditCard, title: "Gérer les paiements / cartes des membres",
    desc: "Gère les abonnements, cartes sauvegardées Stripe et prélèvements.",
    to: "/app/membres-paiements", cta: "Paiements membres",
  },
  {
    icon: Calculator, title: "Comptabilité & taxes (TPS/TVQ)",
    desc: "Reçus, revenus Stripe synchronisés, rapprochement des taxes.",
    to: "/app/comptabilite-kaizen", cta: "Comptabilité Kaizen",
  },
  {
    icon: RefreshCw, title: "Migration des anciens membres (ZenPlanner)",
    desc: "Importer les alumni en leads + envoyer les liens de paiement Stripe de transition.",
    to: "/app/zp-migration", cta: "Migration ZenPlanner",
  },
  {
    icon: Users, title: "Voir / éditer mes élèves & contrats",
    desc: "Liste des élèves, statuts, disciplines et contrats.",
    to: "/app/eleves", cta: "Élèves & contrats",
  },
  {
    icon: CalendarDays, title: "Revenus en ligne (Kaizen Online)",
    desc: "Graphique 6 mois, export CSV, abonnés au curriculum en ligne.",
    to: "/app/revenus-en-ligne", cta: "Revenus en ligne",
  },
];

const ETE_STATUS = [
  { label: "Page publique « Horaire d'été 2026 »", state: "ok", note: `${PUBLIC_HOST}/horaire-ete-2026` },
  { label: "Code promo membres ETE2026 (taxes juillet/sept.)", state: "ok", note: "À appliquer au paiement" },
  { label: "Hub public « Été 2026 »", state: "ok", note: `${PUBLIC_HOST}/ete-2026` },
  { label: "Diffusion courriel « Horaire d'été »", state: "todo", note: "Modèle prêt — à envoyer depuis Diffusion membres (en prod)" },
  { label: "Diffusion SMS de masse", state: "ok", note: "Module construit — prêt à l'emploi" },
  { label: "Générateur campagne pub « Les 3 »", state: "wip", note: "Backend en place — UI à finaliser" },
];

const STATE_BADGE = {
  ok: ["✅ Prêt", "bg-emerald-100 text-emerald-700"],
  wip: ["🛠️ En cours", "bg-amber-100 text-amber-700"],
  todo: ["⏳ À faire", "bg-blue-100 text-blue-700"],
};

const CREDIT_TIPS = [
  "Sois précis dans tes demandes : « change le bouton X en rouge » coûte moins qu'une demande vague.",
  "Le redéploiement ne coûte PAS de crédits — déploie sans crainte après chaque lot de changements.",
  "Utilise le Rollback (gratuit) pour revenir à une version précédente au lieu de tout refaire.",
  "Explore ton code gratuitement avec le « VS Code View » d'Emergent (aucun crédit).",
  "Regroupe plusieurs petites demandes en une seule au lieu d'aller-retours multiples.",
];

export default function GuideModule() {
  useEffect(() => { document.title = "Guide & Aide-mémoire — Kaizen"; }, []);
  return (
    <div className="space-y-6" data-testid="guide-module">
      <PageHeader title="🧭 Guide & Aide-mémoire" subtitle="Où aller pour chaque tâche · état des chantiers · trucs pour économiser tes crédits." icon={Compass} />

      <SectionCard title="Où aller pour…" icon={Compass}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="guide-tasks">
          {TASKS.map((t, i) => {
            const Icon = t.icon;
            return (
              <Link key={i} to={t.to} data-testid={`guide-task-${i}`}
                className="group border border-zinc-800 rounded-xl p-4 flex items-start gap-3 hover:border-[#D90429] hover:shadow-sm transition-colors">
                <div className="shrink-0 w-10 h-10 rounded-lg bg-[#D90429]/10 flex items-center justify-center">
                  <Icon size={18} className="text-[#D90429]" />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-zinc-100">{t.title}</p>
                  <p className="text-sm text-zinc-400 mt-0.5">{t.desc}</p>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#D90429] mt-2">
                    {t.cta} <ArrowRight size={13} className="group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </SectionCard>

      <SectionCard title="État du chantier « Été 2026 »" icon={CalendarDays}>
        <div className="space-y-2" data-testid="guide-ete-status">
          {ETE_STATUS.map((s, i) => {
            const [label, cls] = STATE_BADGE[s.state];
            return (
              <div key={i} className="flex items-center justify-between gap-3 border border-zinc-800 rounded-lg p-3">
                <div className="min-w-0">
                  <p className="font-semibold text-zinc-200 text-sm">{s.label}</p>
                  <p className="text-xs text-zinc-400 truncate">{s.note}</p>
                </div>
                <span className={`text-[11px] px-2 py-1 rounded-full font-semibold whitespace-nowrap ${cls}`}>{label}</span>
              </div>
            );
          })}
        </div>
      </SectionCard>

      <SectionCard title="Économiser tes crédits Emergent" icon={Lightbulb}>
        <ul className="space-y-2" data-testid="guide-credit-tips">
          {CREDIT_TIPS.map((tip, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
              <Lightbulb size={15} className="text-amber-500 mt-0.5 shrink-0" /> {tip}
            </li>
          ))}
        </ul>
        <p className="text-xs text-zinc-500 mt-3">Rappel : le preview (développement) et {PUBLIC_HOST} (production) ont des bases de données séparées. Les changements n'apparaissent en prod qu'après un redéploiement.</p>
      </SectionCard>
    </div>
  );
}
